import Actions from 'Actions';
import { ReduxPayload, ComponentType } from 'types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { cloneDeep, get } from 'lodash';

import { migrateVif } from 'common/visualizations/helpers/migrateVif';
import MetadataProvider from 'common/visualizations/dataProviders/MetadataProvider';

import Environment from 'StorytellerEnvironment';
import type { StorytellerState } from 'store/StorytellerReduxState';
import { storyReducerStoriesSelector, storyReducerBlocksSelector } from 'store/TopLevelSelector';

/**
 * Actions that will be handled by multiple reducers should go here.
 */

export const createStory = createAction<ReduxPayload>(Actions.STORY_CREATE);
export const saveStory = createAction<ReduxPayload>(Actions.STORY_SAVED);

export const addFilterParameterConfiguration = createAction<ReduxPayload>(
  Actions.FILTER_PARAMETER_CONFIGURATION_ADDED,
);
export const deleteFilterParameterConfiguration = createAction<ReduxPayload>(
  Actions.FILTER_PARAMETER_CONFIGURATION_DELETED,
);
export const updateFilterParameterConfiguration = createAction<ReduxPayload>(
  Actions.FILTER_PARAMETER_CONFIGURATION_UPDATED,
);
export const updateAllFilterParameterConfigurations = createAction<ReduxPayload>(
  Actions.ALL_FILTER_PARAMETER_CONFIGURATIONS_UPDATED,
);

// New actions for draft filter state management
export const resetDraftFilterConfigurations = createAction<void>(Actions.RESET_DRAFT_FILTER_CONFIGURATIONS);
export const updateDraftFilterParameterConfiguration = createAction<ReduxPayload>(
  Actions.DRAFT_FILTER_PARAMETER_CONFIGURATION_UPDATED,
);
export const updateAllDraftFilterParameterConfigurations = createAction<ReduxPayload>(
  Actions.ALL_DRAFT_FILTER_PARAMETER_CONFIGURATIONS_UPDATED,
);
export const prepareStoryForTemplate = createAction<ReduxPayload>(Actions.STORY_PREPARE_FOR_TEMPLATE);
export const replaceStoryFromJsonImport = createAction<ReduxPayload>(Actions.STORY_REPLACE_FROM_JSON_IMPORT);
export const replaceStoryFromTemplate = createAction<ReduxPayload>(Actions.STORY_REPLACE_FROM_TEMPLATE);

export const removeStoryDataSource = createAction<ReduxPayload>(Actions.STORY_DATA_SOURCE_REMOVED);
export const updateStoryDataSource = createAction<ReduxPayload>(Actions.STORY_DATA_SOURCE_UPDATED);

export const removeGlobalFilter = createAction<void>(Actions.GLOBAL_FILTER_REMOVED);

/**
 * Async thunks
 */

export const refreshRequiredDataSources = createAsyncThunk<
  // Return type
  { requiredDataSources: string[] },
  // Payload type
  void,
  // thunkAPI type
  {
    state: StorytellerState;
  }
>(Actions.REFRESH_REQUIRED_DATA_SOURCES, async (_, thunkAPI) => {
  const stories = storyReducerStoriesSelector(thunkAPI.getState()) || {};
  const requiredDataSources: string[] = [];

  if (Environment.STORY_UID) {
    const { blockIds = [] } = stories[Environment.STORY_UID];
    const blocks = storyReducerBlocksSelector(thunkAPI.getState()) || {};
    const vizCanDatasets: string[] = [];
    const platformMeasuresDatasets: string[] = [];

    blockIds.forEach((blockId) => {
      const block = blocks[blockId] || {};
      const { components = [] } = block;
      components.forEach((component) => {
        // If there is VIF available, add to requiredDataSources
        const vif = get(component, 'value.vif');
        if (vif) {
          const migratedVif = migrateVif(cloneDeep(vif));
          const datasetUid: string = get(migratedVif, 'series[0].dataSource.datasetUid');
          if (datasetUid && !requiredDataSources.includes(datasetUid)) {
            requiredDataSources.push(datasetUid);
          }
        }

        if (component.type === ComponentType.MEASURE_CARD || component.type === ComponentType.MEASURE_CHART) {
          // if in situ measure
          if (component.value?.isInsitu) {
            const datasetUid: string = component.value?.measure?.dataSourceLensUid;
            if (datasetUid && !requiredDataSources.includes(datasetUid)) {
              requiredDataSources.push(datasetUid);
            }
          } else {
            // If platform measure
            const datasetUid: string = component.value?.measure?.uid;
            if (datasetUid && !platformMeasuresDatasets.includes(datasetUid)) {
              platformMeasuresDatasets.push(datasetUid);
            }
          }
        }

        // If there is VizCan, get its dataset into vizCanDatasets
        if (component.type === ComponentType.VIZ_CANVAS) {
          // For vizCan, we don't have vif, so we need to get it from value
          const { datasetUid }: { datasetUid: string } = component.value?.dataset ?? {};
          if (datasetUid && !vizCanDatasets.includes(datasetUid)) {
            vizCanDatasets.push(datasetUid);
          }
        }
      });
    });

    // For VizCan datasets, get its VIF from api/views and add to requiredDataSources
    if (vizCanDatasets.length > 0) {
      const metadataProviderPromises = vizCanDatasets.map((datasetUid) => {
        return new MetadataProvider({ datasetUid }, true).getDatasetMetadata();
      });
      const views = await Promise.all(metadataProviderPromises);
      views.forEach((view) => {
        const vif = get(view, 'displayFormat.visualizationCanvasMetadata.vifs[0]');
        if (vif) {
          const migratedVif = migrateVif(cloneDeep(vif));
          const datasetUid = get(migratedVif, 'series[0].dataSource.datasetUid');
          if (datasetUid && !requiredDataSources.includes(datasetUid)) {
            requiredDataSources.push(datasetUid);
          }
        }
      });
    }

    // For platform measures, get their datasets from api/measures and add to requiredDataSources
    if (platformMeasuresDatasets.length > 0) {
      const metadataProviderPromises = platformMeasuresDatasets.map((measureUid) => {
        return new MetadataProvider({ datasetUid: measureUid }, true).getPlatformMeasure();
      });

      const measures = await Promise.all(metadataProviderPromises);
      measures.forEach((measure) => {
        const datasetUid = measure?.dataSourceLensUid;
        if (datasetUid && !requiredDataSources.includes(datasetUid)) {
          requiredDataSources.push(datasetUid);
        }
      });
    }
  }

  return { requiredDataSources };
});
